import {
  ADAPTIVE_SECONDARY_BACKGROUND,
  ADAPTIVE_SECONDARY_FOREGROUND,
  type ColorMode
} from '@color';
import { size } from '@geom';
import type { ImageSource } from '@image';
import { ObjectFit } from '@image';
import { LayoutConstants } from '@layout';
import { IString, type LocaleId } from '@strings';
import { BasicTextStyle } from '@typography';
import { assertInstanceOf, expectDefined, produce } from '@utils';
import type { VisualCache } from '@visual';
import type { SnorfLayout } from './SnorfLayout';
import type { SnorfSpec } from './SnorfSpec';
import { Snorf } from './transforms';
import { determineLayoutDirectionFromText } from './types';

export const ENTITY_PADDING = 'entity-padding';
export const ENTITY_TOP = 'entity-top';
export const ENTITY_TITLE = 'entity-title';
export const ENTITY_HEADER = 'entity-header';
export const ENTITY_TYPE = 'entity-type';
export const ENTITY_CONTENT = 'entity-content';
export const ENTITY_ICON = 'entity-icon';
export const ENTITY_HEADER_SPACER = 'entity-header-spacer';

const HEADER_PADDING = { leading: 10, trailing: 12, top: 6, bottom: 6 };
const CONTENT_PADDING = 12;
const INITIAL_WIDTH = 400;
// Use global layout constants for icon sizing and gap to keep math consistent
const ICON_SIZE = LayoutConstants.UNIT_ICON_SIZE.width;
const ICON_GAP = LayoutConstants.UNIT_ICON_GAP;
const CORNER_RADIUS = 5;
const HEADER_CORNER_RADII = {
  topLeft: CORNER_RADIUS,
  topRight: CORNER_RADIUS,
  bottomLeft: 0,
  bottomRight: 0
};
const CONTENT_CORNER_RADII = {
  topLeft: 0,
  topRight: 0,
  bottomLeft: CORNER_RADIUS,
  bottomRight: CORNER_RADIUS
};

export interface EntityHeader {
  className: IString;
  backgroundColor: string;
}

export interface EntitySpec {
  header?: EntityHeader;
  title: IString;
  icon?: ImageSource;
  backgroundColor?: string;
}

/**
 * Creates a `SnorfSpec` that mimics Flying Logic's Entity pattern using JSON API:
 * - Top colored bar with entity type using FixedFontSizeChooser (constant font size) - OPTIONAL
 * - Main content area with descriptive text using ScalingFontSizeChooser (adaptive font size)
 * - Different background colors for different entity types
 * - Proper border handling between sections
 * - Uses color mode agnostic choices that adapt to light/dark modes
 * - Performs complete layout calculation including width adjustment
 * - Automatically detects text direction and uses appropriate SNORF layout for RTL support
 */
export function createEntityLayoutSpec(spec: EntitySpec, padding = 0): SnorfSpec {
  const entityTop = {
    id: ENTITY_TOP,
    direction: 'top-to-bottom',
    width: { type: 'fit', size: { min: LayoutConstants.UNIT_MIN_SIZE.width } },
    height: {
      type: 'fit',
      size: { min: LayoutConstants.UNIT_MIN_SIZE.height }
    },
    backgroundColor: spec.backgroundColor ?? ADAPTIVE_SECONDARY_BACKGROUND,
    border: { width: 2, color: ADAPTIVE_SECONDARY_FOREGROUND },
    clip: true,
    cornerRadius: CORNER_RADIUS,
    childAlignment: { x: 'center' },
    children: spec.header
      ? [entityHeader(spec.header), entityContent(spec)]
      : [entityContent(spec)]
  };

  if (padding <= 0) {
    // No padding, just return the top entity layout
    return Snorf.fromJSON({
      elements: [entityTop]
    });
  } else {
    return Snorf.fromJSON({
      elements: [
        {
          id: ENTITY_PADDING,
          direction: 'top-to-bottom',
          padding,
          children: [entityTop]
        }
      ]
    });
  }
}

export function layoutEntity(
  entitySpec: EntitySpec,
  snorfSpec: SnorfSpec,
  colorMode: ColorMode,
  locale: LocaleId,
  cache: VisualCache,
  opts?: { mark?: string }
): SnorfLayout {
  const mark = opts?.mark;

  const _spec = produce<SnorfSpec>(snorfSpec, draft => {
    const titleElem = draft.getElement(ENTITY_TITLE);
    const title = expectDefined(titleElem.textData).text.get(locale);
    const contentDirection = determineLayoutDirectionFromText(title);
    titleElem.layoutConfig.direction = contentDirection;

    if (draft.hasElement(ENTITY_HEADER)) {
      const headerElem = draft.getElement(ENTITY_HEADER);
      const headerText = headerElem.textData?.text.get(locale) ?? '';
      const headerDirection = determineLayoutDirectionFromText(headerText);
      headerElem.layoutConfig.direction = headerDirection;
    }
  });
  // TWO-PHASE LAYOUT: First measure text naturally, then constrain to natural width
  // Phase 1: Initial layout to measure natural text dimensions
  const layout = _spec.performLayout(size(INITIAL_WIDTH, 300), colorMode, locale, cache, {
    mark: `${mark}-pass-1`
  });

  // Get layout elements to determine natural width (same logic as original)
  const titleLayoutElement = layout.getLayoutElement(ENTITY_TITLE);

  // Determine natural width based on measured text (dimensions reflect measured.size)
  let titleWidth = titleLayoutElement.dimensions.width + CONTENT_PADDING * 2;

  // If icon exists, account for icon width and gap in content calculation
  if (entitySpec.icon) {
    titleWidth += ICON_SIZE + ICON_GAP;
  }
  let naturalWidth = titleWidth;

  // If header exists, include its width in the calculation
  if (entitySpec.header) {
    const typeLayoutElement = layout.getLayoutElement(ENTITY_TYPE);
    const headerTypeWidth = typeLayoutElement.dimensions.width;
    const headerWidth =
      headerTypeWidth + HEADER_PADDING.leading + HEADER_PADDING.trailing;

    naturalWidth = Math.max(headerWidth, naturalWidth);
  }

  // Phase 2: Create a new spec with fixed widths
  const finalSpec = produce<SnorfSpec>(_spec, draft => {
    // Fix header width so the colored bar spans the entity width
    if (entitySpec.header) {
      const headerDecl = draft.getElement(ENTITY_HEADER);
      headerDecl.layoutConfig.sizing.width.fixed(naturalWidth);
    }

    // Fix content width so text can be properly aligned within the available space
    const contentDecl = draft.getElement(ENTITY_CONTENT);
    contentDecl.layoutConfig.sizing.width.fixed(naturalWidth);

    const titleText = expectDefined(layout.getLayoutElement(ENTITY_TITLE).textData);
    const textMaxWidth = entitySpec.icon
      ? naturalWidth - CONTENT_PADDING * 2 - ICON_SIZE - ICON_GAP // Account for icon and gap
      : naturalWidth - CONTENT_PADDING * 2;
    // Only constrain max width so the row can hug content and be centered visually
    titleText.layoutOptions.maxWidth = textMaxWidth;
  });

  // Phase 2: Recalculate layout with fixed header and content widths
  return finalSpec.performLayout(size(naturalWidth, 300), colorMode, locale, cache, {
    mark: `${mark}-pass-2`
  });
}

export function createEntityLayout(
  entitySpec: EntitySpec,
  colorMode: ColorMode,
  locale: LocaleId,
  cache: VisualCache,
  padding = 20
): SnorfLayout {
  const snorfSpec = createEntityLayoutSpec(entitySpec, padding);
  return layoutEntity(entitySpec, snorfSpec, colorMode, locale, cache);
}

function entityHeader(header: EntityHeader): unknown {
  return {
    id: ENTITY_HEADER,
    padding: HEADER_PADDING,
    childAlignment: { y: 'center' },
    backgroundColor: header.backgroundColor,
    cornerRadius: HEADER_CORNER_RADII,
    zIndex: 1,
    children: [
      {
        id: ENTITY_TYPE,
        textData: {
          text: header.className,
          color: `fg-${header.backgroundColor}`,
          layoutOptions: {
            style: BasicTextStyle.BOLD,
            fontSizeChooser: { type: 'fixed', fontSize: 14 },
            // Keep on a single line without expanding parent; allow ellipsis if needed
            maxLines: 1
          }
        }
      }
    ]
  };
}

function entityIcon(entitySpec: EntitySpec): unknown {
  return {
    id: ENTITY_ICON,
    width: LayoutConstants.UNIT_ICON_SIZE.width,
    height: LayoutConstants.UNIT_ICON_SIZE.height,
    imageData: {
      source: entitySpec.icon,
      objectFit: ObjectFit.CONTAIN,
      opacity: 1.0
    }
  };
}

function entityTitle(entitySpec: EntitySpec): unknown {
  assertInstanceOf(entitySpec.title, IString);
  const color = `fg-${entitySpec.backgroundColor ?? ADAPTIVE_SECONDARY_BACKGROUND}`;
  return {
    id: ENTITY_TITLE,
    textData: {
      text: entitySpec.title,
      color,
      layoutOptions: {
        fontSizeChooser: {
          type: 'scaling',
          fontSizeRange: [20, 12],
          taperCharCountRange: [4, 80]
        }
      }
    }
  };
}

function entityContent(entitySpec: EntitySpec): unknown {
  // Determine if we need an icon layout or simple text layout
  if (entitySpec.icon) {
    // Outer container spans full width; row inside hugs content and is centered
    return {
      id: ENTITY_CONTENT,
      direction: 'top-to-bottom',
      padding: CONTENT_PADDING,
      childAlignment: { x: 'center' },
      backgroundColor: entitySpec.backgroundColor ?? ADAPTIVE_SECONDARY_BACKGROUND,
      cornerRadius: CONTENT_CORNER_RADII,
      children: [
        {
          id: `${ENTITY_CONTENT}-row`,
          childGap: LayoutConstants.UNIT_ICON_GAP,
          childAlignment: { y: 'center' },
          children: [entityIcon(entitySpec), entityTitle(entitySpec)]
        }
      ]
    };
  } else {
    // Simple text-only layout (either no icon or icon not loaded)
    return {
      id: ENTITY_CONTENT,
      direction: 'top-to-bottom',
      padding: CONTENT_PADDING,
      childAlignment: { x: 'center' },
      backgroundColor: entitySpec.backgroundColor ?? ADAPTIVE_SECONDARY_BACKGROUND,
      cornerRadius: {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 8,
        bottomRight: 8
      },
      children: [
        {
          id: `${ENTITY_CONTENT}-row`,
          childAlignment: { y: 'center' },
          children: [entityTitle(entitySpec)]
        }
      ]
    };
  }
}
